import { FC, useEffect } from "react";

import { isGroupAccessId, isStudentAccessId } from "../../../shared/access-id";

import { ResponseType, useResponsesForPage } from "./responses";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { IconText } from "mds/components/IconText";
import { Listbox, type ListboxEntry } from "mds/components/Listbox";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { PersonClassIcon, PersonGroupIcon, PersonSingleIcon } from "mds/icons";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { usePresentation } from "utils/presentation";
import { useQueryParamsActions } from "utils/routing";

type ClassResponsesDropdownProps = {
  pageId: string;
};

// TODO: Look at combining this with the StudentDropdown component
export const ClassResponsesDropdown: FC<ClassResponsesDropdownProps> = ({ pageId }) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const { responses } = useResponsesForPage(pageId);
  const { presentingRollup, isFollowing, presentedPageId, presentedAccessId } = usePresentation();
  const pageIsPresenting = presentedPageId === pageId;
  const response = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);
  const isMdOrLarger = useIsMdOrLarger();

  // If we are following a user response and the presenter
  // changes it we need to update the selected response we are seeing
  useEffect(() => {
    if (presentingRollup && pageIsPresenting && isFollowing) {
      if (presentedAccessId) {
        setQueryParams({
          [ACCESS_ID_QUERY_PARAM]: presentedAccessId,
          [SUBMISSION_ID_QUERY_PARAM]: null,
        });
      } else {
        deleteQueryParams([ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM]);
      }
    }
  }, [
    pageIsPresenting,
    presentedAccessId,
    presentingRollup,
    isFollowing,
    setQueryParams,
    deleteQueryParams,
  ]);

  const onSelectionChange = (item: ResponseType) => {
    if (item.id) {
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: item.id, [SUBMISSION_ID_QUERY_PARAM]: null });
    } else {
      deleteQueryParams([ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM]);
    }
  };

  const initialText = response?.title || responses[0]?.title;
  const MenuIcon = isStudentAccessId(response?.id) ? (
    <PersonSingleIcon className="group-data-[selected]/tab:text-blue" />
  ) : isGroupAccessId(response?.id) ? (
    <PersonGroupIcon className="group-data-[selected]/tab:text-blue" />
  ) : (
    <PersonClassIcon className="group-data-[selected]/tab:text-blue" />
  );

  const buttonText = isMdOrLarger ? (
    <IconText iconStart={MenuIcon} text={initialText} />
  ) : (
    initialText
  );

  // Divider after the first item ("All Responses")
  const items: Array<ListboxEntry<ResponseType>> = responses[0]
    ? [responses[0], { type: "divider" }, ...responses.slice(1)]
    : responses;

  return (
    <div className="flex items-center">
      <Listbox
        buttonSize="s"
        buttonText={buttonText}
        className="responses-dropdown"
        eventName="Class Responses - Student page selected"
        items={items}
        openMenuEventName="Class Responses - All responses dropdown"
        selectedValue={response?.id}
        onSelectionChange={(item) => onSelectionChange(item)}
      />
    </div>
  );
};
